import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, getStudentId, setStudentId, clearStudentId, goToGoogleLogin } from "@/lib/api";

export default function LinkAccount() {
  const navigate = useNavigate();
  const studentId = getStudentId();
  const [student, setStudent] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!studentId) {
      navigate("/onboarding");
      return;
    }
    api
      .get(`/students/${studentId}`)
      .then(({ data }) => {
        if (data.email) {
          navigate("/login");
          return;
        }
        setStudent(data);
        setChecking(false);
      })
      .catch(() => {
        clearStudentId();
        navigate("/onboarding");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || password.length < 6) {
      toast.error("Please enter a valid email and a password with at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", { email: email.trim(), password, student_id: studentId });
      setStudentId(data.id);
      toast.success("Your progress is now saved to your account!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create your account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="link-account-loading">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-4 relative overflow-hidden" data-testid="link-account-page">
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-secondary/20 rounded-full blur-3xl" />
      <div className="relative z-10 w-full max-w-md bg-card rounded-3xl shadow-2xl p-6 sm:p-8 border border-border">
        <div className="w-14 h-14 rounded-2xl bg-success/15 flex items-center justify-center mb-4">
          <ShieldCheck className="text-success" size={26} />
        </div>
        <h1 className="font-heading font-extrabold text-2xl mb-2" data-testid="link-account-heading">
          Secure your progress, {student?.name}!
        </h1>
        <p className="text-muted-foreground text-sm mb-6">
          You've earned {student?.xp || 0} XP so far. Create a free account with your email and password so you
          never lose it — and can log back in anytime.
        </p>
        <button
          type="button"
          onClick={() => goToGoogleLogin({ student_id: studentId })}
          data-testid="link-account-google-button"
          className="w-full h-12 rounded-xl border border-border bg-white text-[#1F1F1F] font-semibold flex items-center justify-center gap-2.5 mb-5 hover:bg-white/90 active:scale-95 transition-all"
        >
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.6 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z" />
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.6 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4c-7.7 0-14.3 4.3-17.7 10.7z" />
            <path fill="#4CAF50" d="M24 44c5.3 0 10.1-1.8 13.9-5l-6.4-5.4C29.5 35.4 26.9 36 24 36c-5.3 0-9.9-3.4-11.5-8.1l-6.6 5.1C9.6 39.6 16.2 44 24 44z" />
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.1-3.5 5.7-6.4 7.3l6.4 5.4C39.4 37.2 44 31.5 44 24c0-1.3-.1-2.7-.4-3.5z" />
          </svg>
          Continue with Google
        </button>
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="link-account-form">
          <div>
            <Label htmlFor="link-email">Email</Label>
            <Input id="link-email" type="email" data-testid="link-account-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@gmail.com" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="link-password">Password</Label>
            <Input id="link-password" type="password" data-testid="link-account-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters" className="mt-1.5" />
          </div>
          <Button type="submit" data-testid="link-account-submit-button" disabled={loading} className="w-full h-12 rounded-xl text-base font-semibold">
            {loading ? "Saving..." : "Save My Progress"}
          </Button>
        </form>
      </div>
    </div>
  );
}
